// Persisted, clamped per-column pixel widths for resizable feed columns.
// Widths survive reload via localStorage; corrupt or missing values fall
// back to the provided defaults.

import { useCallback, useEffect, useState } from 'react';

export type ColumnKey = 'service' | 'version' | 'time';
export type ColumnWidths = Record<ColumnKey, number>;

export interface ColumnConstraint {
  min: number;
  max: number;
}

export type ColumnConstraints = Record<ColumnKey, ColumnConstraint>;

const clamp = (value: number, { min, max }: ColumnConstraint) =>
  Math.max(min, Math.min(max, value));

const load = (
  storageKey: string,
  defaults: ColumnWidths,
  constraints: ColumnConstraints
): ColumnWidths => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    const out = { ...defaults };
    (Object.keys(defaults) as ColumnKey[]).forEach(key => {
      const v = parsed?.[key];
      if (typeof v === 'number' && Number.isFinite(v)) {
        out[key] = clamp(v, constraints[key]);
      }
    });
    return out;
  } catch {
    return defaults;
  }
};

export const useColumnWidths = (
  storageKey: string,
  defaults: ColumnWidths,
  constraints: ColumnConstraints
) => {
  const [widths, setWidths] = useState<ColumnWidths>(() =>
    load(storageKey, defaults, constraints)
  );

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(widths));
    } catch {
      // storage unavailable — keep in-memory only
    }
  }, [storageKey, widths]);

  const setWidth = useCallback(
    (key: ColumnKey, px: number) => {
      setWidths(prev => ({ ...prev, [key]: clamp(px, constraints[key]) }));
    },
    [constraints]
  );

  const resetWidth = useCallback(
    (key: ColumnKey) => {
      setWidths(prev => ({ ...prev, [key]: defaults[key] }));
    },
    [defaults]
  );

  return { widths, setWidth, resetWidth };
};
